import { PLATFORM } from "../config/platform";
import { useState } from "react";
import type { FormEvent } from "react";

import { useDispatch } from "react-redux";

import type { AppDispatch } from "../redux/store/store";

import { setTokens } from "../redux/slice/authSlice";
import { loginAs } from "../redux/slice/layoutSlice";

import { useLoginMutation } from "../redux/api/platform/tokenApi";
import { IconLock, IconEye, IconEyeOff } from "../components/icons";
import { validateEmail } from "../utils/validation";
import type { XRole } from "../config/module-types";

// Derive a display name from the email local-part ("jane.doe" -> "Jane Doe").
const nameFromEmail = (email: string) =>
  email
    .split("@")[0]
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ") || email;

// Hand the just-used credentials to the browser's password manager (Google
// Password Manager, iCloud Keychain, etc.). In a SPA there's no navigation on
// submit, so the Credential Management API is the reliable way to trigger the
// "Save password?" prompt; the form's autocomplete attributes cover browsers
// without it. Best-effort and non-fatal — requires a secure context.
const offerToSaveCredential = async (
  id: string,
  password: string,
  name?: string,
) => {
  try {
    const Ctor = (window as unknown as {
      PasswordCredential?: new (data: {
        id: string;
        password: string;
        name?: string;
      }) => Credential;
    }).PasswordCredential;

    if (Ctor && navigator.credentials && "store" in navigator.credentials) {
      const credential = new Ctor({ id, password, name: name || id });
      await navigator.credentials.store(credential);
    }
  } catch {
    // The browser may not support it or may block the request — ignore.
  }
};

interface LoginProps {
  /** Return to the public landing page. */
  onBack?: () => void;
}

export const Login = ({ onBack }: LoginProps = {}) => {
  const dispatch = useDispatch<AppDispatch>();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [login, { isLoading }] = useLoginMutation();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    const emailError = validateEmail(email);
    if (emailError) {
      setError(emailError);
      return;
    }

    if (!password) {
      setError("Password is required.");
      return;
    }

    try {
      const tokens = await login({ email, password }).unwrap();

      // Offer the credentials to the browser's password manager before the
      // authenticated shell replaces this form.
      await offerToSaveCredential(
        tokens.email || email,
        password,
        tokens.name || nameFromEmail(email),
      );

      // Persist tokens (authSlice -> localStorage) and flip the layout session
      // that App.tsx uses to render the authenticated shell. The role is
      // resolved by the backend `login` (broker / platform owner / org role),
      // defaulting to "user" if absent.
      dispatch(setTokens(tokens));
      dispatch(
        loginAs({
          name: tokens.name || nameFromEmail(email),
          email: tokens.email || email,
          role: (tokens.role as XRole) || "user",
          organizationId: tokens.organizationId ?? undefined,
        }),
      );
    } catch (err) {
      const message =
        (err as { message?: string })?.message ?? "Invalid email or password";
      setError(message);
    }
  };

  return (
    <div className="login">
      <form className="login__card" onSubmit={handleSubmit}>
        <span className="login__logo">
          <IconLock size={26} />
        </span>
        <h1 className="login__title">{PLATFORM.platform_name}</h1>
        <p className="login__sub">Sign in to your account</p>

        <label className="login__field">
          Email
          <input
            type="email"
            name="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="username"
          />
        </label>

        <label className="login__field">
          Password
          <span className="login__password">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
            <button
              type="button"
              className="login__password-toggle"
              onClick={() => setShowPassword((s) => !s)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              title={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <IconEyeOff size={18} /> : <IconEye size={18} />}
            </button>
          </span>
        </label>

        <button
          type="submit"
          className="btn btn--primary login__submit"
          disabled={isLoading}
        >
          {isLoading ? "Signing In…" : "Sign In"}
        </button>

        {error ? (
          <p className="login__hint login__hint--error">{error}</p>
        ) : (
          <p className="login__hint">Use your services account credentials.</p>
        )}

        {onBack ? (
          <button
            type="button"
            className="btn btn--ghost login__back"
            onClick={onBack}
          >
            ← Back to home
          </button>
        ) : null}
      </form>
    </div>
  );
};
